import * as signalR from '@microsoft/signalr';
import { ChatMessage } from '../types/chat';

export interface SignalRServiceCallbacks {
  onMessageReceived?: (message: ChatMessage) => void;
  onUserJoined?: (tripId: string) => void;
  onUserLeft?: (tripId: string) => void;
  onError?: (error: string) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private callbacks: SignalRServiceCallbacks = {};
  private currentTripId: string | null = null;
  private maxReconnectAttempts = 5;

  constructor(private hubUrl: string, private getToken: () => string | null) {}

  // Initialize connection
  async start(): Promise<void> {
    console.log('🔵 [SignalR] start() called');
    console.log('🔵 [SignalR] Current connection state:', this.connection?.state);
    
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      console.log('🟢 [SignalR] Already connected, skipping start');
      return;
    }

    const token = this.getToken();
    console.log('🔵 [SignalR] Token available:', token ? `Yes (${token.substring(0, 20)}...)` : 'No');
    
    if (!token) {
      console.error('❌ [SignalR] No authentication token available');
      throw new Error('No authentication token available');
    }

    console.log('🔵 [SignalR] Creating connection to:', this.hubUrl);
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => {
          const t = this.getToken();
          console.log('🔵 [SignalR] accessTokenFactory called, token:', t ? `Yes (${t.substring(0, 20)}...)` : 'No');
          return t || '';
        },
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount < this.maxReconnectAttempts) {
            return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
          }
          return null; // Stop reconnecting
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    console.log('🔵 [SignalR] Connection builder created, setting up event handlers...');
    // Register event handlers
    this.setupEventHandlers();

    try {
      console.log('🔵 [SignalR] Starting connection...');
      await this.connection.start();
      console.log('✅ [SignalR] Connection started successfully');
      console.log('🔵 [SignalR] Connection ID:', this.connection.connectionId);
      console.log('🔵 [SignalR] Connection State:', this.connection.state);
      
      this.callbacks.onConnected?.();
      console.log('✅ [SignalR] Connected and callbacks.onConnected called');

      // Rejoin current trip chat if exists
      if (this.currentTripId) {
        await this.joinTripChat(this.currentTripId);
      }
    } catch (error) {
      console.error('❌ SignalR Connection Error:', error);
      this.callbacks.onError?.('Failed to connect to chat server');
      throw error;
    }
  }

  // Setup event handlers
  private setupEventHandlers(): void {
    if (!this.connection) {
      console.error('❌ [SignalR] setupEventHandlers: connection is null');
      return;
    }

    console.log('🔵 [SignalR] Setting up event handlers...');

    // Message received
    this.connection.on('ReceiveMessage', (message: any) => {
      console.log('📨 [SignalR] ===== ReceiveMessage event fired =====');
      console.log('📨 [SignalR] Raw message received:', JSON.stringify(message, null, 2));
      console.log('📨 [SignalR] Message type:', typeof message);
      console.log('📨 [SignalR] Message keys:', Object.keys(message || {}));
      
      // Transform the message to match ChatMessage interface
      const chatMessage: ChatMessage = {
        id: message.id,
        sender: message.sender || message.Sender,
        timestamp: message.timestamp || message.Timestamp,
        type: message.type || message.Type || 'text',
        content: message.content || message.Content,
        senderType: message.senderType ?? message.SenderType ?? 1,
        viewState: message.viewState ?? message.ViewState ?? 0,
        attachments: (message.attachments || message.Attachments || []).map((att: any) => ({
          fileName: att.fileName || att.FileName || att.name || 'file',
          fileUrl: att.fileUrl || att.FileUrl || att.url || '',
          thumbnailUrl: att.thumbnailUrl || att.ThumbnailUrl
        }))
      };
      
      console.log('📨 [SignalR] Transformed message:', JSON.stringify(chatMessage, null, 2));
      console.log('🔵 [SignalR] Calling onMessageReceived callback...');
      console.log('🔵 [SignalR] Callback exists:', !!this.callbacks.onMessageReceived);
      
      if (this.callbacks.onMessageReceived) {
        this.callbacks.onMessageReceived(chatMessage);
        console.log('✅ [SignalR] onMessageReceived callback executed');
      } else {
        console.warn('⚠️ [SignalR] onMessageReceived callback is not set!');
      }
      
      console.log('📨 [SignalR] ===== End ReceiveMessage handler =====');
    });

    // User joined
    this.connection.on('UserJoined', (tripId: string) => {
      console.log('👤 [SignalR] UserJoined event fired, tripId:', tripId);
      this.callbacks.onUserJoined?.(tripId);
    });

    // Error handler
    this.connection.on('Error', (error: string) => {
      console.error('❌ [SignalR] Error event fired:', error);
      this.callbacks.onError?.(error);
    });
    
    console.log('✅ [SignalR] Event handlers registered: ReceiveMessage, UserJoined, Error');

    // Reconnection events
    this.connection.onreconnecting((error) => {
      console.log('🔄 [SignalR] Reconnecting...', error);
    });

    this.connection.onreconnected((connectionId) => {
      console.log('✅ [SignalR] Reconnected, connectionId:', connectionId);
      // Rejoin current trip chat
      if (this.currentTripId) {
        console.log('🟡 [SignalR] Rejoining trip chat after reconnect:', this.currentTripId);
        this.joinTripChat(this.currentTripId);
      }
    });

    this.connection.onclose((error) => {
      console.log('🔌 [SignalR] Connection Closed', error);
      console.log('🔵 [SignalR] Close error details:', error ? JSON.stringify(error, null, 2) : 'No error');
      this.callbacks.onDisconnected?.();
    });
  }

  // Join a trip's chat room
  async joinTripChat(tripId: string): Promise<void> {
    console.log('🔵 [SignalR] joinTripChat() called with tripId:', tripId);
    console.log('🔵 [SignalR] Connection exists:', !!this.connection);
    console.log('🔵 [SignalR] Connection state:', this.connection?.state);
    
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.log('🟡 [SignalR] Connection not ready, starting...');
      await this.start();
    }

    try {
      // Leave previous trip if exists
      if (this.currentTripId && this.currentTripId !== tripId) {
        console.log('🟡 [SignalR] Leaving previous trip:', this.currentTripId);
        await this.leaveTripChat(this.currentTripId);
      }

      console.log('🔵 [SignalR] Invoking JoinTripChat with tripId:', tripId);
      if (!this.connection) {
        throw new Error('Connection is null');
      }
      await this.connection.invoke('JoinTripChat', tripId);
      this.currentTripId = tripId;
      console.log(`✅ [SignalR] Successfully joined trip chat: ${tripId}`);
    } catch (error) {
      console.error('❌ [SignalR] Error joining trip chat:', error);
      console.error('❌ [SignalR] Error details:', JSON.stringify(error, null, 2));
      throw error;
    }
  }

  // Leave a trip's chat room
  async leaveTripChat(tripId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke('LeaveTripChat', tripId);
      if (this.currentTripId === tripId) {
        this.currentTripId = null;
      }
      console.log(`👋 Left trip chat: ${tripId}`);
    } catch (error) {
      console.error('❌ Error leaving trip chat:', error);
    }
  }

  // Send a message
  async sendMessage(
    tripId: string,
    content: string,
    type: string = 'text',
    attachments?: Array<{ fileName: string; fileUrl: string }>
  ): Promise<void> {
    console.log('🔵 [SignalR] ===== sendMessage() called =====');
    console.log('🔵 [SignalR] Parameters:', {
      tripId,
      content,
      type,
      attachmentsCount: attachments?.length || 0,
      attachments: attachments
    });
    console.log('🔵 [SignalR] Connection exists:', !!this.connection);
    console.log('🔵 [SignalR] Connection state:', this.connection?.state);
    console.log('🔵 [SignalR] isConnected():', this.isConnected());
    
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.error('❌ [SignalR] Connection is not established!');
      console.error('❌ [SignalR] Connection state:', this.connection?.state);
      throw new Error('SignalR connection is not established');
    }

    try {
      const payload = {
        tripId,
        content,
        type,
        attachments: attachments || []
      };
      console.log('🔵 [SignalR] Invoking SendMessage with payload:', JSON.stringify(payload, null, 2));
      console.log('🔵 [SignalR] Connection ID:', this.connection.connectionId);
      
      await this.connection.invoke('SendMessage', tripId, content, type, attachments || []);
      console.log('✅ [SignalR] SendMessage invoke completed successfully');
      console.log('🔵 [SignalR] ===== End sendMessage() =====');
    } catch (error) {
      console.error('❌ [SignalR] Error sending message:', error);
      console.error('❌ [SignalR] Error details:', JSON.stringify(error, null, 2));
      console.error('❌ [SignalR] Error stack:', error instanceof Error ? error.stack : 'No stack');
      throw error;
    }
  }

  // Set callbacks
  setCallbacks(callbacks: SignalRServiceCallbacks): void {
    console.log('🔵 [SignalR] setCallbacks() called');
    console.log('🔵 [SignalR] Callbacks provided:', {
      onMessageReceived: !!callbacks.onMessageReceived,
      onUserJoined: !!callbacks.onUserJoined,
      onUserLeft: !!callbacks.onUserLeft,
      onError: !!callbacks.onError,
      onConnected: !!callbacks.onConnected,
      onDisconnected: !!callbacks.onDisconnected
    });
    this.callbacks = { ...this.callbacks, ...callbacks };
    console.log('✅ [SignalR] Callbacks set successfully');
  }

  // Stop connection
  async stop(): Promise<void> {
    if (this.currentTripId) {
      await this.leaveTripChat(this.currentTripId);
    }

    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
    console.log('🔌 SignalR connection stopped');
  }

  // Get connection state
  getConnectionState(): signalR.HubConnectionState {
    return this.connection?.state ?? signalR.HubConnectionState.Disconnected;
  }

  // Check if connected
  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

// Export singleton instance
let signalRServiceInstance: SignalRService | null = null;

export const createSignalRService = (
  hubUrl: string,
  getToken: () => string | null
): SignalRService => {
  if (!signalRServiceInstance) {
    signalRServiceInstance = new SignalRService(hubUrl, getToken);
  }
  return signalRServiceInstance;
};

export const getSignalRService = (): SignalRService | null => {
  return signalRServiceInstance;
};

